/**
 * ChartCommandHandler - Phase 4.1: Unified Chart Command System
 *
 * Provides comprehensive AI command handling for all 8 chart types:
 * - line-chart, bar-chart, pie-chart, area-chart
 * - scatter-chart, gauge-chart, heatmap-chart, treemap-chart
 *
 * Features:
 * - Title and axis label modification
 * - Color themes and custom palettes
 * - Data series management (CRUD)
 * - Chart type conversion with property mapping
 * - Axes configuration (min/max, grid, legend)
 * - Animation controls
 *
 * @see Documents/AI_Template_System/Roadmaps/AI_Chat_Enhancement_Roadmap.md
 */

import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { chartTypeRegistry } from "../../charts/ChartTypeRegistry";
import { chartDataManager, ChartDataPoint } from "../../charts/ChartDataManager";
import { chartTemplateManager } from "../../charts/templates/ChartTemplates";
import { ChartNavigationConfig, ChartNavigationMapping } from "@/types/chartNavigation";
import { useEditorStore } from "@/stores/editor/editor-store";
import { logger } from "@/utils/logger";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

const isChartDataPoint = (value: unknown): value is ChartDataPoint =>
  isRecord(value);

const getChartDataPoints = (value: unknown): ChartDataPoint[] =>
  Array.isArray(value) ? value.filter(isChartDataPoint) : [];

const isChartNavigationMapping = (value: unknown): value is ChartNavigationMapping =>
  isRecord(value) && typeof value.targetPageId === "string";

const getChartNavigationConfig = (value: unknown): ChartNavigationConfig | undefined => {
  if (!isRecord(value) || typeof value.enabled !== "boolean" || !Array.isArray(value.mappings)) {
    return undefined;
  }

  return {
    enabled: value.enabled,
    mappings: value.mappings.filter(isChartNavigationMapping),
    defaultAction: value.defaultAction === "navigate" || value.defaultAction === "none"
      ? value.defaultAction
      : undefined,
    defaultTarget: typeof value.defaultTarget === "string" ? value.defaultTarget : undefined,
    passDataAsParams: typeof value.passDataAsParams === "boolean" ? value.passDataAsParams : undefined,
  };
};

// ============================================================================
// Phase 4.1.5: Chart Theme Presets
// ============================================================================

/**
 * Predefined color themes for charts
 * Each theme provides 5 colors for data series visualization
 */
export const CHART_THEMES: Record<string, string[]> = {
  // Agricultural/Nature themes
  agricultural: ["#2D5A3D", "#4A7C59", "#6B9E6F", "#8DC08F", "#AEDEB0"],
  forest: ["#1B4332", "#2D6A4F", "#40916C", "#52B788", "#74C69D"],

  // Professional themes
  blue: ["#1E40AF", "#3B82F6", "#60A5FA", "#93C5FD", "#BFDBFE"],
  ocean: ["#0077BE", "#0099CC", "#00BBDD", "#00DDDD", "#00EEEE"],
  corporate: ["#1F2937", "#374151", "#4B5563", "#6B7280", "#9CA3AF"],

  // Warm themes
  sunset: ["#DC2626", "#EA580C", "#F59E0B", "#FCD34D", "#FEF3C7"],
  autumn: ["#B45309", "#D97706", "#F59E0B", "#FBBF24", "#FDE68A"],

  // Vibrant themes
  rainbow: ["#EF4444", "#F59E0B", "#22C55E", "#3B82F6", "#8B5CF6"],
  pastel: ["#FCA5A5", "#FCD34D", "#86EFAC", "#93C5FD", "#C4B5FD"],
  vibrant: ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7"],

  // Standard themes
  red: ["#DC2626", "#EF4444", "#F87171", "#FCA5A5", "#FECACA"],
  green: ["#16A34A", "#22C55E", "#4ADE80", "#86EFAC", "#BBF7D0"],
  purple: ["#7C3AED", "#8B5CF6", "#A78BFA", "#C4B5FD", "#DDD6FE"],
  orange: ["#EA580C", "#F97316", "#FB923C", "#FDBA74", "#FED7AA"],
  yellow: ["#CA8A04", "#EAB308", "#FACC15", "#FDE047", "#FEF08A"],

  // Monochrome
  grayscale: ["#1F2937", "#374151", "#6B7280", "#9CA3AF", "#D1D5DB"],
  monochrome: ["#000000", "#333333", "#666666", "#999999", "#CCCCCC"],
};

/**
 * Theme aliases for natural language matching
 */
const THEME_ALIASES: Record<string, string> = {
  // Agricultural
  farm: "agricultural",
  agriculture: "agricultural",
  nature: "forest",
  tree: "forest",
  woods: "forest",

  // Water/Ocean
  water: "ocean",
  sea: "ocean",
  aqua: "ocean",
  marine: "ocean",

  // Professional
  business: "corporate",
  professional: "corporate",
  dark: "grayscale",
  gray: "grayscale",
  grey: "grayscale",

  // Warm
  warm: "sunset",
  fire: "sunset",
  fall: "autumn",

  // Cool
  cool: "blue",
  cold: "blue",

  // Vibrant
  bright: "vibrant",
  colorful: "rainbow",
  multicolor: "rainbow",
  spectrum: "rainbow",

  // Soft
  soft: "pastel",
  light: "pastel",
  muted: "pastel",
};

// ============================================================================
// Gauge Threshold Parsing
// ============================================================================

interface GaugeThreshold {
  min: number;
  max: number;
  color: string;
  label?: string;
}

const THRESHOLD_COLOR_MAP: Record<string, string> = {
  red: "#EF4444", danger: "#EF4444", critical: "#EF4444", low: "#EF4444",
  yellow: "#F59E0B", warning: "#F59E0B", caution: "#F59E0B", amber: "#F59E0B",
  green: "#10B981", normal: "#10B981", ok: "#10B981", good: "#10B981", safe: "#10B981",
  orange: "#F97316",
  blue: "#3B82F6",
  purple: "#8B5CF6",
  gray: "#6B7280", grey: "#6B7280",
};

const THRESHOLD_LABEL_MAP: Record<string, string> = {
  red: "Danger", danger: "Danger", critical: "Critical", low: "Low",
  yellow: "Warning", warning: "Warning", caution: "Caution", amber: "Warning",
  green: "Normal", normal: "Normal", ok: "OK", good: "Good", safe: "Safe",
  orange: "Alert",
  blue: "Info",
  purple: "Special",
  gray: "Inactive", grey: "Inactive",
};

/**
 * Parse threshold zone definitions from a user's natural language input.
 * Supports formats:
 *   "red 0-49, yellow 50-64, green 65-80"
 *   "#EF4444 0-49 #F59E0B 50-64 #10B981 65-80"
 *   "danger 0-50, warning 50-75, normal 75-100"
 */
function parseThresholdZones(input: string): GaugeThreshold[] | null {
  const thresholds: GaugeThreshold[] = [];

  // Match color/name followed by a numeric range (e.g., "red 0-49" or "#EF4444 0-49")
  const zonePattern = /(?:(#[0-9A-Fa-f]{3,6})|([a-z]+))\s+(\d+(?:\.\d+)?)\s*[-–to]+\s*(\d+(?:\.\d+)?)/gi;
  let match: RegExpExecArray | null;

  while ((match = zonePattern.exec(input)) !== null) {
    const hexColor = match[1];
    const namedColor = match[2]?.toLowerCase();
    const min = parseFloat(match[3]);
    const max = parseFloat(match[4]);

    if (isNaN(min) || isNaN(max) || min >= max) continue;

    let color: string;
    let label: string | undefined;

    if (hexColor) {
      color = hexColor;
    } else if (namedColor && THRESHOLD_COLOR_MAP[namedColor]) {
      color = THRESHOLD_COLOR_MAP[namedColor];
      label = THRESHOLD_LABEL_MAP[namedColor];
    } else {
      continue; // unrecognized color name
    }

    thresholds.push({ min, max, color, label });
  }

  return thresholds.length > 0 ? thresholds : null;
}

// ============================================================================
// Phase 2: Responsive Chart Presets
// ============================================================================

/**
 * Phase 2.2: Responsive chart presets for quick configuration
 * These presets optimize chart display for different device sizes
 */
const RESPONSIVE_CHART_PRESETS: Record<string, Record<string, unknown>> = {
  'compact': {
    showLegend: false,
    showGrid: false,
    margin: { top: 5, right: 5, left: 5, bottom: 5 },
    axisFontSize: 8,
  },
  'minimal': {
    showLegend: false,
    showGrid: false,
    showXAxis: false,
    showYAxis: false,
    margin: { top: 0, right: 0, left: 0, bottom: 0 },
  },
  'comfortable': {
    showLegend: true,
    showGrid: true,
    margin: { top: 15, right: 20, left: 15, bottom: 15 },
    axisFontSize: 10,
  },
  'detailed': {
    showLegend: true,
    showGrid: true,
    showTooltip: true,
    showDots: true,
    margin: { top: 20, right: 30, left: 20, bottom: 20 },
    axisFontSize: 12,
  },
  'full': {
    showLegend: true,
    showGrid: true,
    showTooltip: true,
    showDots: true,
    showXAxis: true,
    showYAxis: true,
    axisFontSize: 12,
    legendFontSize: 12,
  },
};

// ============================================================================
// Phase 4.1.3: Chart Type Conversion Mappings
// ============================================================================

/**
 * Chart conversion compatibility matrix
 * Defines which chart types can convert to which, and what properties are preserved
 */
interface ChartConversionConfig {
  preservedProperties: string[];
  propertyMappings: Record<string, string>;
  warnings: string[];
  dataTransformRequired: boolean;
}

const CHART_CONVERSIONS: Record<string, ChartConversionConfig> = {
  // Line chart conversions
  "line-bar": {
    preservedProperties: ["data", "colors", "title", "showLegend", "showGrid", "xAxisLabel", "yAxisLabel"],
    propertyMappings: {},
    warnings: [],
    dataTransformRequired: false,
  },
  "line-area": {
    preservedProperties: ["data", "colors", "title", "showLegend", "showGrid", "xAxisLabel", "yAxisLabel", "strokeWidth"],
    propertyMappings: {},
    warnings: [],
    dataTransformRequired: false,
  },
  "line-scatter": {
    preservedProperties: ["data", "colors", "title", "showLegend", "showGrid"],
    propertyMappings: { strokeWidth: "dotSize" },
    warnings: ["Line connections will be removed"],
    dataTransformRequired: true,
  },

  // Bar chart conversions
  "bar-line": {
    preservedProperties: ["data", "colors", "title", "showLegend", "showGrid", "xAxisLabel", "yAxisLabel"],
    propertyMappings: {},
    warnings: [],
    dataTransformRequired: false,
  },
  "bar-pie": {
    preservedProperties: ["data", "colors", "title", "showLegend"],
    propertyMappings: {},
    warnings: ["Only first data series will be shown", "Axis labels will be removed"],
    dataTransformRequired: true,
  },
  "bar-area": {
    preservedProperties: ["data", "colors", "title", "showLegend", "showGrid", "xAxisLabel", "yAxisLabel"],
    propertyMappings: { stacked: "stacked" },
    warnings: [],
    dataTransformRequired: false,
  },

  // Pie chart conversions
  "pie-bar": {
    preservedProperties: ["data", "colors", "title", "showLegend"],
    propertyMappings: {},
    warnings: ["Inner radius setting will be lost"],
    dataTransformRequired: false,
  },
  "pie-treemap": {
    preservedProperties: ["data", "colors", "title"],
    propertyMappings: {},
    warnings: [],
    dataTransformRequired: true,
  },

  // Area chart conversions
  "area-line": {
    preservedProperties: ["data", "colors", "title", "showLegend", "showGrid", "xAxisLabel", "yAxisLabel"],
    propertyMappings: {},
    warnings: ["Fill will be removed"],
    dataTransformRequired: false,
  },
  "area-bar": {
    preservedProperties: ["data", "colors", "title", "showLegend", "showGrid", "xAxisLabel", "yAxisLabel", "stacked"],
    propertyMappings: {},
    warnings: [],
    dataTransformRequired: false,
  },

  // Scatter chart conversions
  "scatter-line": {
    preservedProperties: ["data", "colors", "title", "showLegend", "showGrid"],
    propertyMappings: { dotSize: "strokeWidth" },
    warnings: ["Points will be connected with lines"],
    dataTransformRequired: true,
  },
};

// ============================================================================
// Chart Command Handler Class
// ============================================================================

export class ChartCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    logger.debug("Initializing ChartCommandHandler patterns");

    // ========================================================================
    // Phase 2.1: Responsive Breakpoint Patterns
    // IMPORTANT: These patterns must be added FIRST because they have ^ anchors
    // and need to be matched before patterns without anchors (like "hide legend")
    // which could match anywhere in the input string.
    // ========================================================================

    // Pattern 1: "on mobile/tablet/desktop" prefix
    // Examples: "on mobile hide legend", "on tablet thick lines", "on desktop show grid"
    this.commandPatterns.set(
      /^on\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => {
        console.log(`[Phase 2.1] Matched "on breakpoint" pattern: breakpoint=${match[1]}, subCommand="${match[2]}"`);
        return this.handleChartBreakpointCommand(match[1], match[2], component);
      }
    );

    // Pattern 2: Colon syntax "mobile: command"
    // Examples: "mobile: hide legend", "tablet: use thick lines", "desktop: show all"
    this.commandPatterns.set(
      /^(mobile|tablet|desktop):\s*(.+)$/i,
      (match, component) => {
        console.log(`[Phase 2.1] Matched "breakpoint:" pattern: breakpoint=${match[1]}, subCommand="${match[2]}"`);
        return this.handleChartBreakpointCommand(match[1], match[2], component);
      }
    );

    // Pattern 3: "only on X" exclusive syntax
    // Examples: "only on mobile hide legend", "only on tablet compact"
    this.commandPatterns.set(
      /^only\s+on\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => {
        console.log(`[Phase 2.1] Matched "only on breakpoint" pattern: breakpoint=${match[1]}, subCommand="${match[2]}"`);
        return this.handleChartBreakpointCommand(match[1], match[2], component);
      }
    );

    // Pattern 4: Simple prefix for chart keywords (no colon, no "on")
    // Examples: "mobile hide legend", "tablet thick lines", "desktop show grid"
    this.commandPatterns.set(
      /^(mobile|tablet|desktop)\s+(hide|show|legend|grid|title|axis|dots|markers|thick|thin|small|large|compact|minimal|detailed|full)\b(.*)$/i,
      (match, component) => {
        const subCommand = match[2] + (match[3] || '');
        console.log(`[Phase 2.1] Matched simple prefix pattern: breakpoint=${match[1]}, subCommand="${subCommand.trim()}"`);
        return this.handleChartBreakpointCommand(match[1], subCommand.trim(), component);
      }
    );

    // ========================================================================
    // Phase 4.1.2: Title and Label Patterns
    // ========================================================================

    // Chart title patterns
    this.commandPatterns.set(
      /(?:set |change )?(?:chart )?title (?:to )?["']([^"']+)["']/i,
      (match) => {
        console.log(`[Phase 4.1] Setting chart title to: ${match[1]}`);
        return { properties: { title: match[1] } };
      }
    );

    this.commandPatterns.set(
      /(?:set |change )?(?:chart )?title (?:to )?(.+)/i,
      (match) => {
        const title = match[1].trim().replace(/^["']|["']$/g, "");
        console.log(`[Phase 4.1] Setting chart title to: ${title}`);
        return { properties: { title } };
      }
    );

    // X-axis label patterns
    this.commandPatterns.set(
      /(?:set |change )?x[\s-]?axis (?:label )?(?:to )?["']([^"']+)["']/i,
      (match) => {
        console.log(`[Phase 4.1] Setting x-axis label to: ${match[1]}`);
        return { properties: { xAxisLabel: match[1] } };
      }
    );

    this.commandPatterns.set(
      /(?:change |set )?(?:the )?x[\s-]?axis (?:label )?to ["']?([^"']+)["']?/i,
      (match) => {
        const label = match[1].trim().replace(/^["']|["']$/g, "");
        console.log(`[Phase 4.1] Setting x-axis label to: ${label}`);
        return { properties: { xAxisLabel: label } };
      }
    );

    // Y-axis label patterns
    this.commandPatterns.set(
      /(?:set |change )?y[\s-]?axis (?:label )?(?:to )?["']([^"']+)["']/i,
      (match) => {
        console.log(`[Phase 4.1] Setting y-axis label to: ${match[1]}`);
        return { properties: { yAxisLabel: match[1] } };
      }
    );

    this.commandPatterns.set(
      /(?:change |set )?(?:the )?y[\s-]?axis (?:label )?to ["']?([^"']+)["']?/i,
      (match) => {
        const label = match[1].trim().replace(/^["']|["']$/g, "");
        console.log(`[Phase 4.1] Setting y-axis label to: ${label}`);
        return { properties: { yAxisLabel: label } };
      }
    );

    // ========================================================================
    // Phase 4.1.2 & 4.1.5: Color and Theme Patterns
    // ========================================================================

    // Theme patterns (named themes)
    this.commandPatterns.set(
      /(?:use |apply |set )?(?:the )?(\w+)\s*(?:color |colour )?theme/i,
      (match) => {
        const themeName = match[1].toLowerCase();
        const resolvedTheme = THEME_ALIASES[themeName] || themeName;

        if (CHART_THEMES[resolvedTheme]) {
          console.log(`[Phase 4.1] Applying theme: ${resolvedTheme}`);
          return { properties: { colors: CHART_THEMES[resolvedTheme] } };
        }
        console.log(`[Phase 4.1] Theme not found: ${themeName}`);
        return null;
      }
    );

    // Apply colors pattern (named theme)
    this.commandPatterns.set(
      /apply (\w+) colou?rs?/i,
      (match) => {
        const themeName = match[1].toLowerCase();
        const resolvedTheme = THEME_ALIASES[themeName] || themeName;

        if (CHART_THEMES[resolvedTheme]) {
          console.log(`[Phase 4.1] Applying colors from theme: ${resolvedTheme}`);
          return { properties: { colors: CHART_THEMES[resolvedTheme] } };
        }
        return null;
      }
    );

    // Set colors to array pattern
    this.commandPatterns.set(
      /set colou?rs? (?:to )?\[([^\]]+)\]/i,
      (match) => {
        const colorString = match[1];
        const colors = colorString
          .split(/[,\s]+/)
          .map(c => c.trim().replace(/["']/g, ""))
          .filter(c => /^#[0-9A-Fa-f]{3,6}$/.test(c) || /^(rgb|hsl)/i.test(c));

        if (colors.length > 0) {
          console.log(`[Phase 4.1] Setting custom colors: ${colors.join(", ")}`);
          return { properties: { colors } };
        }
        return null;
      }
    );

    // Hex colors in command
    this.commandPatterns.set(
      /(?:use |set )?colou?rs? (?:to )?((?:#[0-9A-Fa-f]{3,6}[\s,]*)+)/i,
      (match) => {
        const hexColors = match[1].match(/#[0-9A-Fa-f]{3,6}/g);
        if (hexColors && hexColors.length > 0) {
          console.log(`[Phase 4.1] Setting hex colors: ${hexColors.join(", ")}`);
          return { properties: { colors: hexColors } };
        }
        return null;
      }
    );

    // Set chart colors to value list
    this.commandPatterns.set(
      /set chart colou?rs? to (.+)/i,
      (match) => {
        const colors = this.parseChartColorList(match[1]);
        if (colors.length > 0) {
          console.log(`[Phase 4.1] Setting chart colors: ${colors.join(", ")}`);
          return { properties: { colors } };
        }
        return null;
      }
    );

    // Set chart background color
    this.commandPatterns.set(
      /set chart background (?:to |color to )?(.+)/i,
      (match) => {
        const resolvedColor = this.parseColorValue(match[1].trim());
        if (resolvedColor) {
          console.log(`[Phase 4.1] Setting chart background color: ${resolvedColor}`);
          return { style: { backgroundColor: resolvedColor } };
        }
        return null;
      }
    );

    // Set grid color
    this.commandPatterns.set(
      /set grid color (?:to )?(.+)/i,
      (match) => {
        const resolvedColor = this.parseColorValue(match[1].trim());
        if (resolvedColor) {
          console.log(`[Phase 4.1] Setting grid color: ${resolvedColor}`);
          return { properties: { gridColor: resolvedColor } };
        }
        return null;
      }
    );

    // Basic color theme keywords
    this.commandPatterns.set(
      /(?:use |apply |set )?(?:color |theme |palette )?(?:to )?(blue|red|green|purple|orange|yellow|rainbow|pastel|ocean|agricultural|sunset|forest|corporate|grayscale)/i,
      (match) => {
        const theme = match[1].toLowerCase();
        if (CHART_THEMES[theme]) {
          console.log(`[Phase 4.1] Applying color theme: ${theme}`);
          return { properties: { colors: CHART_THEMES[theme] } };
        }
        return null;
      }
    );

    // ========================================================================
    // Phase 4.1.2: Legend and Grid Patterns
    // ========================================================================

    // Legend patterns
    this.commandPatterns.set(
      /(show|hide|display|remove|enable|disable) (?:the )?legend/i,
      (match) => {
        const action = match[1].toLowerCase();
        const show = ["show", "display", "enable"].includes(action);
        console.log(`[Phase 4.1] ${show ? "Showing" : "Hiding"} legend`);
        return { properties: { showLegend: show } };
      }
    );

    // Grid patterns
    this.commandPatterns.set(
      /(show|hide|display|remove|enable|disable) (?:the )?grid(?:\s*lines?)?/i,
      (match) => {
        const action = match[1].toLowerCase();
        const show = ["show", "display", "enable"].includes(action);
        console.log(`[Phase 4.1] ${show ? "Showing" : "Hiding"} grid lines`);
        return { properties: { showGrid: show } };
      }
    );

    // Tooltip patterns
    this.commandPatterns.set(
      /(show|hide|display|remove|enable|disable) (?:the )?tooltips?/i,
      (match) => {
        const action = match[1].toLowerCase();
        const show = ["show", "display", "enable"].includes(action);
        console.log(`[Phase 4.1] ${show ? "Showing" : "Hiding"} tooltips`);
        return { properties: { showTooltip: show } };
      }
    );

    // X-axis visibility patterns
    this.commandPatterns.set(
      /(show|hide|display|remove|enable|disable) (?:the )?x[\s-]?axis/i,
      (match) => {
        const action = match[1].toLowerCase();
        const show = ["show", "display", "enable"].includes(action);
        console.log(`[Phase 4.1] ${show ? "Showing" : "Hiding"} x-axis`);
        return { properties: { showXAxis: show } };
      }
    );

    // Y-axis visibility patterns
    this.commandPatterns.set(
      /(show|hide|display|remove|enable|disable) (?:the )?y[\s-]?axis/i,
      (match) => {
        const action = match[1].toLowerCase();
        const show = ["show", "display", "enable"].includes(action);
        console.log(`[Phase 4.1] ${show ? "Showing" : "Hiding"} y-axis`);
        return { properties: { showYAxis: show } };
      }
    );

    // Both axes visibility patterns
    this.commandPatterns.set(
      /(show|hide|display|remove|enable|disable) (?:the |both )?axes/i,
      (match) => {
        const action = match[1].toLowerCase();
        const show = ["show", "display", "enable"].includes(action);
        console.log(`[Phase 4.1] ${show ? "Showing" : "Hiding"} both axes`);
        return { properties: { showXAxis: show, showYAxis: show } };
      }
    );

    // ========================================================================
    // Phase 4.1.2: Animation Patterns
    // ========================================================================

    this.commandPatterns.set(
      /(enable|disable|add|remove|turn on|turn off) (?:the )?animations?/i,
      (match) => {
        const action = match[1].toLowerCase();
        const enabled = ["enable", "add", "turn on"].includes(action);
        console.log(`[Phase 4.1] ${enabled ? "Enabling" : "Disabling"} animation`);
        return { properties: { animation: enabled } };
      }
    );

    this.commandPatterns.set(
      /animations? (on|off)/i,
      (match) => {
        const enabled = match[1].toLowerCase() === "on";
        console.log(`[Phase 4.1] Animation ${enabled ? "on" : "off"}`);
        return { properties: { animation: enabled } };
      }
    );

    // ========================================================================
    // Phase 4.1.2: Axis Range Patterns
    // ========================================================================

    // Y-axis minimum
    this.commandPatterns.set(
      /(?:set |start )?y[\s-]?axis (?:at |from |minimum |min )?(-?\d+)/i,
      (match) => {
        const min = parseInt(match[1], 10);
        console.log(`[Phase 4.1] Setting y-axis minimum to: ${min}`);
        return { properties: { yAxisMin: min } };
      }
    );

    this.commandPatterns.set(
      /start y[\s-]?axis (?:at )?(-?\d+)/i,
      (match) => {
        const min = parseInt(match[1], 10);
        console.log(`[Phase 4.1] Starting y-axis at: ${min}`);
        return { properties: { yAxisMin: min } };
      }
    );

    // Y-axis maximum
    this.commandPatterns.set(
      /(?:set )?y[\s-]?axis (?:maximum |max |to |end at |ends? at )(-?\d+)/i,
      (match) => {
        const max = parseInt(match[1], 10);
        console.log(`[Phase 4.1] Setting y-axis maximum to: ${max}`);
        return { properties: { yAxisMax: max } };
      }
    );

    this.commandPatterns.set(
      /end y[\s-]?axis (?:at )?(-?\d+)/i,
      (match) => {
        const max = parseInt(match[1], 10);
        console.log(`[Phase 4.1] Ending y-axis at: ${max}`);
        return { properties: { yAxisMax: max } };
      }
    );

    // Y-axis range
    this.commandPatterns.set(
      /y[\s-]?axis (?:from |range )?(-?\d+) to (-?\d+)/i,
      (match) => {
        const min = parseInt(match[1], 10);
        const max = parseInt(match[2], 10);
        console.log(`[Phase 4.1] Setting y-axis range: ${min} to ${max}`);
        return { properties: { yAxisMin: min, yAxisMax: max } };
      }
    );

    // ========================================================================
    // Line Chart Specific Patterns
    // ========================================================================

    // Phase 1 thickness presets - must match LineChartHandler
    const thicknessPresets: Record<string, number> = {
      'hairline': 0.5, 'thin': 1, 'thinner': 1, 'light': 1.5,
      'normal': 2, 'default': 2, 'medium': 2.5,
      'thick': 3, 'thicker': 3, 'heavy': 4, 'bold': 4,
      'extra-thick': 5, 'extrathick': 5, 'ultra-thick': 6, 'ultrathick': 6,
    };

    // Double/halve patterns - must come BEFORE preset patterns
    this.commandPatterns.set(
      /\b(double|halve)\b.*(?:thickness|width|stroke|line)/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const currentWidth = typeof component.properties?.strokeWidth === 'number'
          ? component.properties.strokeWidth : 2;
        const newWidth = action === 'double'
          ? Math.min(10, currentWidth * 2)
          : Math.max(0.5, currentWidth / 2);
        console.log(`[Phase 4.1] ${action} thickness: ${currentWidth} → ${newWidth}`);
        return { properties: { strokeWidth: newWidth } };
      }
    );

    // Increase/decrease by X patterns - must come BEFORE preset patterns
    this.commandPatterns.set(
      /\b(increase|decrease)\b.*(?:thickness|width|stroke).*\bby\s+(\d+(?:\.\d+)?)/i,
      (match, component) => {
        const action = match[1].toLowerCase();
        const amount = parseFloat(match[2]);
        const currentWidth = typeof component.properties?.strokeWidth === 'number'
          ? component.properties.strokeWidth : 2;
        const newWidth = action === 'increase'
          ? Math.min(10, currentWidth + amount)
          : Math.max(0.5, currentWidth - amount);
        console.log(`[Phase 4.1] ${action} thickness by ${amount}: ${currentWidth} → ${newWidth}`);
        return { properties: { strokeWidth: newWidth } };
      }
    );

    // Named preset patterns - use word boundary to prevent "thick" matching "thickness"
    // Support both "thin line" and "thin lines" variations
    this.commandPatterns.set(
      /(?:make |set |use )?(?:line |lines |a )?\b(hairline|thin|thinner|light|normal|default|medium|thick|thicker|heavy|bold|extra-?thick|ultra-?thick)\b(?:\s+(?:lines?|width|stroke))?$/i,
      (match) => {
        const preset = match[1].toLowerCase().replace('-', '');
        const strokeWidth = thicknessPresets[preset] ?? 2;
        console.log(`[Phase 4.1] Setting line thickness preset "${preset}": ${strokeWidth}`);
        return { properties: { strokeWidth } };
      }
    );

    this.commandPatterns.set(
      /(?:set )?(?:line )?(?:stroke )?width (?:to )?(\d+(?:\.\d+)?)/i,
      (match) => {
        const strokeWidth = parseFloat(match[1]);
        console.log(`[Phase 4.1] Setting stroke width to: ${strokeWidth}`);
        return { properties: { strokeWidth: Math.min(10, Math.max(0.5, strokeWidth)) } };
      }
    );

    // Phase 1 line style presets - must match LineChartHandler
    const lineStylePresets: Record<string, string> = {
      'solid': '', 'continuous': '',
      'dashed': '5 5', 'dotted': '2 2', 'dot': '2 2',
      'long-dash': '10 5', 'longdash': '10 5',
      'short-dash': '3 3', 'shortdash': '3 3',
      'dash-dot': '5 2 2 2', 'dashdot': '5 2 2 2',
      'dash-dot-dot': '5 2 2 2 2 2', 'dashdotdot': '5 2 2 2 2 2',
      'sparse': '15 10', 'dense': '2 1', 'morse': '10 2 2 2 10 2',
    };

    // Line style pattern - use word boundary to prevent matching partial words like "dots" vs "dot"
    this.commandPatterns.set(
      /(?:make |use )?(?:line |lines )?(?:style )?(solid|continuous|dashed|dotted|dot|long-?dash|short-?dash|dash-?dot-?dot|dash-?dot|sparse|dense|morse)\b(?:\s+(?:style|line))?/i,
      (match) => {
        const style = match[1].toLowerCase().replace(/-/g, '');
        const strokeDasharray = lineStylePresets[style] ?? lineStylePresets[match[1].toLowerCase()] ?? '';
        console.log(`[Phase 4.1] Setting line style to: ${match[1]} → "${strokeDasharray}"`);
        return { properties: { strokeDasharray } };
      }
    );

    // Phase 1 line opacity patterns - must come before gauge patterns
    const opacityPresets: Record<string, number> = {
      'translucent': 0.7, 'faded': 0.5, 'semi-transparent': 0.5,
      'semitransparent': 0.5, 'subtle': 0.3, 'ghosted': 0.2, 'ghost': 0.2,
    };

    this.commandPatterns.set(
      /(?:set |make )?(?:line |stroke )?opacity\s+(?:to\s+)?(\d+(?:\.\d+)?)\s*%?/i,
      (match) => {
        let opacity = parseFloat(match[1]);
        // If value > 1, assume it's a percentage
        if (opacity > 1) opacity = opacity / 100;
        opacity = Math.min(1, Math.max(0, opacity));
        console.log(`[Phase 4.1] Setting line opacity to: ${opacity}`);
        return { properties: { strokeOpacity: opacity } };
      }
    );

    this.commandPatterns.set(
      /(?:make |use )?(?:lines? )?(translucent|faded|semi-?transparent|subtle|ghosted|ghost)/i,
      (match) => {
        const preset = match[1].toLowerCase().replace('-', '');
        const opacity = opacityPresets[preset] ?? 0.5;
        console.log(`[Phase 4.1] Setting line opacity preset "${match[1]}": ${opacity}`);
        return { properties: { strokeOpacity: opacity } };
      }
    );

    // Marker visibility patterns - show/hide/add/remove
    this.commandPatterns.set(
      /(show|hide|enable|disable|add|remove) (?:the )?(?:data )?(?:points|dots|markers)/i,
      (match) => {
        const show = ["show", "enable", "add"].includes(match[1].toLowerCase());
        console.log(`[Phase 4.1] ${show ? "Showing" : "Hiding"} data points`);
        return { properties: { showDots: show } };
      }
    );

    // Phase 1 curve type patterns
    const curveTypes: Record<string, string> = {
      'linear': 'linear', 'straight': 'linear', 'angular': 'linear',
      'monotone': 'monotone', 'smooth': 'monotone', 'curved': 'monotone',
      'natural': 'natural',
      'step': 'step', 'stairs': 'step',
      'step-before': 'stepBefore', 'stepbefore': 'stepBefore',
      'step-after': 'stepAfter', 'stepafter': 'stepAfter',
      'basis': 'basis', 'cardinal': 'cardinal',
      'catmull-rom': 'catmullRom', 'catmullrom': 'catmullRom',
    };

    this.commandPatterns.set(
      /(?:make |use )?(?:line |curve |interpolation )?(smooth|curved|linear|straight|angular|monotone|natural|step-?before|step-?after|step|stairs|basis|cardinal|catmull-?rom)(?:\s+(?:curve|interpolation|line))?/i,
      (match) => {
        const input = match[1].toLowerCase().replace(/-/g, '');
        const lineType = curveTypes[input] ?? curveTypes[match[1].toLowerCase()] ?? 'monotone';
        console.log(`[Phase 4.1] Setting curve type "${match[1]}" → ${lineType}`);
        return { properties: { lineType } };
      }
    );

    // ========================================================================
    // Bar Chart Specific Patterns
    // ========================================================================

    this.commandPatterns.set(
      /(?:make |set )?(?:bars? )?(stack|stacked|unstack|unstacked|ungroup|ungrouped|group|grouped|side by side|separate)/i,
      (match) => {
        const style = match[1].toLowerCase();
        // "stack" or "stacked" = stacked: true; all others = stacked: false
        const stacked = style === "stack" || style === "stacked";
        console.log(`[Phase 4.1] Setting bar style to: ${stacked ? "stacked" : "grouped"} (matched: "${style}")`);
        return { properties: { stacked } };
      }
    );

    // Additional pattern for "unstack the bars", "stack them", "show stacked bars"
    this.commandPatterns.set(
      /(stack|unstack|group|ungroup|separate)\s+(?:the\s+)?(?:bars?|them|it)/i,
      (match) => {
        const style = match[1].toLowerCase();
        const stacked = style === "stack";
        console.log(`[Phase 4.1] Setting bar style to: ${stacked ? "stacked" : "grouped"} (phrase: "${match[0]}")`);
        return { properties: { stacked } };
      }
    );

    // Pattern for "show stacked bars", "show grouped bars"
    this.commandPatterns.set(
      /show\s+(stacked|grouped|side\s+by\s+side)\s+bars?/i,
      (match) => {
        const style = match[1].toLowerCase();
        const stacked = style === "stacked";
        console.log(`[Phase 4.1] Setting bar style to: ${stacked ? "stacked" : "grouped"} (show phrase: "${match[0]}")`);
        return { properties: { stacked } };
      }
    );

    // Pattern for "make it a stacked bar chart", "make this a grouped bar chart"
    this.commandPatterns.set(
      /make\s+(?:it|this)\s+(?:a\s+)?(stacked|grouped|unstacked)\s+bar\s+chart/i,
      (match) => {
        const style = match[1].toLowerCase();
        const stacked = style === "stacked";
        console.log(`[Phase 4.1] Setting bar style to: ${stacked ? "stacked" : "grouped"} (make it phrase: "${match[0]}")`);
        return { properties: { stacked } };
      }
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:bars? )?(horizontal|vertical)/i,
      (match) => {
        const orientation = match[1].toLowerCase();
        console.log(`[Phase 4.1] Setting bar orientation to: ${orientation}`);
        return { properties: { layout: orientation, orientation } };
      }
    );

    this.commandPatterns.set(
      /(?:set )?bar (?:gap |spacing )(?:to )?(\d+)/i,
      (match) => {
        const gap = parseInt(match[1], 10);
        console.log(`[Phase 4.1] Setting bar gap to: ${gap}`);
        return { properties: { barGap: gap } };
      }
    );

    // ========================================================================
    // Pie Chart Specific Patterns
    // ========================================================================

    this.commandPatterns.set(
      /(?:make |convert (?:to )?)?(?:a )?donut(?: chart)?/i,
      () => {
        console.log("[Phase 4.1] Converting to donut chart");
        return { properties: { innerRadius: 60 } };
      }
    );

    this.commandPatterns.set(
      /(?:make |convert (?:to )?)?(?:a )?(?:regular |solid )?pie(?: chart)?/i,
      () => {
        console.log("[Phase 4.1] Converting to regular pie chart");
        return { properties: { innerRadius: 0 } };
      }
    );

    this.commandPatterns.set(
      /(?:set )?inner radius (?:to )?(\d+)/i,
      (match) => {
        const radius = parseInt(match[1], 10);
        console.log(`[Phase 4.1] Setting inner radius to: ${radius}`);
        return { properties: { innerRadius: Math.min(100, Math.max(0, radius)) } };
      }
    );

    this.commandPatterns.set(
      /show percentage(?:s)?(?: labels?)?/i,
      () => {
        console.log("[Phase 4.1] Showing percentage labels");
        return { properties: { showLabels: true, labelFormat: "percentage" } };
      }
    );

    this.commandPatterns.set(
      /show value(?:s)?(?: labels?)?/i,
      () => {
        console.log("[Phase 4.1] Showing value labels");
        return { properties: { showLabels: true, labelFormat: "value" } };
      }
    );

    this.commandPatterns.set(
      /(show|hide) (?:slice )?labels?/i,
      (match) => {
        const show = match[1].toLowerCase() === "show";
        console.log(`[Phase 4.1] ${show ? "Showing" : "Hiding"} labels`);
        return { properties: { showLabels: show } };
      }
    );

    // ========================================================================
    // Gauge Chart Specific Patterns
    // ========================================================================

    this.commandPatterns.set(
      /(?:set )?(?:gauge )?value (?:to )?(\d+)/i,
      (match) => {
        const value = parseInt(match[1], 10);
        console.log(`[Phase 4.1] Setting gauge value to: ${value}`);
        return { properties: { value } };
      }
    );

    // Gauge minimum - require "gauge" or "min/minimum" keyword explicitly
    this.commandPatterns.set(
      /(?:set )?(?:gauge\s+)?(?:min(?:imum)?|minimum)\s+(?:to\s+)?(-?\d+)/i,
      (match) => {
        const min = parseInt(match[1], 10);
        console.log(`[Phase 4.1] Setting gauge minimum to: ${min}`);
        return { properties: { min } };
      }
    );

    // Gauge maximum - require "gauge" or "max/maximum" keyword explicitly
    this.commandPatterns.set(
      /(?:set )?(?:gauge\s+)?(?:max(?:imum)?|maximum)\s+(?:to\s+)?(-?\d+)/i,
      (match) => {
        const max = parseInt(match[1], 10);
        console.log(`[Phase 4.1] Setting gauge maximum to: ${max}`);
        return { properties: { max } };
      }
    );

    // Gauge range - "set range 0 to 100", "range -50 to 50"
    this.commandPatterns.set(
      /(?:set\s+)?range\s+(?:from\s+)?(-?\d+)\s+to\s+(-?\d+)/i,
      (match) => {
        const min = parseInt(match[1], 10);
        const max = parseInt(match[2], 10);
        console.log(`[Phase 4.1] Setting gauge range: ${min} to ${max}`);
        return { properties: { min, max } };
      }
    );

    // Gauge unit - "change unit to feet", "set unit to %", "unit to meters"
    // Supports: ' for feet, " for inches, or any text unit
    this.commandPatterns.set(
      /(?:change|set|update)?\s*(?:gauge\s+)?unit\s+(?:to|=|is)\s+(.+?)\s*$/i,
      (match) => {
        let unit = match[1].trim();

        // Map common unit names to symbols
        const unitMappings: Record<string, string> = {
          'feet': "'",
          'foot': "'",
          'ft': "'",
          'inches': '"',
          'inch': '"',
          'in': '"',
        };

        // Check if it's a mapped unit name (case-insensitive)
        const lowerUnit = unit.toLowerCase();
        if (unitMappings[lowerUnit]) {
          unit = unitMappings[lowerUnit];
        } else {
          // Strip surrounding quotes if present (but keep ' or " if that's the entire value)
          if (unit.length > 2 && ((unit.startsWith('"') && unit.endsWith('"')) || (unit.startsWith("'") && unit.endsWith("'")))) {
            unit = unit.slice(1, -1);
          }
        }

        console.log(`[Phase 4.1] Setting gauge unit to: ${unit}`);
        return { properties: { unit } };
      }
    );

    // Gauge threshold patterns
    // Set thresholds: "add thresholds red 0-49, yellow 50-64, green 65-80"
    // Note: Users should separate zones with commas or spaces (not "and") since
    // parseCommand splits on "and" before pattern matching.
    this.commandPatterns.set(
      /(?:add|set|update)\s+(?:gauge\s+)?(?:thresholds?|color\s*zones?|threshold\s*zones?)[\s:]+(.+)/i,
      (match) => {
        const thresholds = parseThresholdZones(match[1]);
        if (thresholds && thresholds.length > 0) {
          console.log(`[Phase 4.1] Setting gauge thresholds: ${thresholds.length} zones`);
          return { properties: { thresholds } };
        }
        console.log(`[Phase 4.1] Could not parse threshold zones from: "${match[1]}"`);
        return null;
      }
    );

    // Remove thresholds: "remove thresholds", "clear color zones"
    this.commandPatterns.set(
      /(?:remove|clear|delete)\s+(?:gauge\s+)?(?:thresholds?|color\s*zones?|threshold\s*zones?)/i,
      () => {
        console.log("[Phase 4.1] Removing gauge thresholds");
        return { properties: { thresholds: undefined } };
      }
    );

    // ========================================================================
    // Phase 6: Gauge Display Mode Switching & Multi-Value Patterns
    // ========================================================================

    // Display mode switching: "switch to ring", "make it a progress bar", "change to milestone"
    this.commandPatterns.set(
      /(?:change|switch|set|convert|make)\s+(?:it\s+)?(?:to\s+)?(?:a?\s*)?(progress\s*ring|ring|progress\s*bar|bar|milestone|dial)\s*(?:mode)?/i,
      (match) => {
        const modeText = match[1].toLowerCase().replace(/\s+/g, '');
        const modeMap: Record<string, string> = {
          'progressring': 'ring', 'ring': 'ring',
          'progressbar': 'bar', 'bar': 'bar',
          'milestone': 'milestone', 'dial': 'dial',
        };
        const displayMode = modeMap[modeText] || 'dial';
        console.log(`[Phase 6] Switching gauge display mode to: ${displayMode}`);
        return { properties: { displayMode } };
      }
    );

    // Concentric rings: "add concentric rings for tasks, goals, velocity"
    this.commandPatterns.set(
      /(?:add|create|set)\s+(?:concentric|nested|multi(?:ple)?)\s+rings?\s+(?:for\s+)?(.+)/i,
      (match) => {
        const labels = match[1].split(/,\s*|\s+and\s+/).map((s: string) => s.trim()).filter(Boolean);
        const defaultColors = ['#10b981', '#3b82f6', '#f59e0b'];
        console.log(`[Phase 6] Setting concentric rings: ${labels.join(', ')}`);
        return {
          properties: {
            displayMode: 'ring',
            concentricValues: labels.slice(0, 3).map((label: string, i: number) => ({
              value: 0, max: 100, color: defaultColors[i % 3], label,
            })),
          },
        };
      }
    );

    // Stacked bar: "show stacked bar with completed, in progress, remaining"
    this.commandPatterns.set(
      /(?:add|create|show|set)\s+(?:a?\s*)?stacked\s+(?:bar|segments?)\s+(?:with|for)\s+(.+)/i,
      (match) => {
        const labels = match[1].split(/,\s*|\s+and\s+/).map((s: string) => s.trim()).filter(Boolean);
        const defaultColors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'];
        console.log(`[Phase 6] Setting stacked bar segments: ${labels.join(', ')}`);
        return {
          properties: {
            displayMode: 'bar',
            stackedSegments: labels.map((label: string, i: number) => ({
              value: 0, color: defaultColors[i % 4], label,
            })),
          },
        };
      }
    );

    // Value format: "show count", "format as {value}/{max}", "show as percentage"
    this.commandPatterns.set(
      /(?:show|display|format)\s+(?:as\s+)?(?:(count|fraction|number|percentage)|(\{.+\}))/i,
      (match) => {
        const presets: Record<string, string> = {
          'count': '{value}', 'fraction': '{value}/{max}',
          'number': '{value}', 'percentage': '{value}%',
        };
        const valueFormat = match[1] ? presets[match[1].toLowerCase()] : match[2];
        console.log(`[Phase 6] Setting value format: ${valueFormat}`);
        return { properties: { valueFormat } };
      }
    );

    // ========================================================================
    // Phase 4.1.4: Data Series Management Patterns
    // ========================================================================

    // Add series
    this.commandPatterns.set(
      /add (?:a )?(?:new )?series ["']([^"']+)["']/i,
      (match) => {
        console.log(`[Phase 4.1] Adding series: ${match[1]}`);
        return {
          properties: {
            _seriesOperation: {
              action: "add",
              seriesName: match[1],
            },
          },
        };
      }
    );

    this.commandPatterns.set(
      /add (?:a )?(?:new )?series (?:called |named )?["']?([^"'\s]+)["']?/i,
      (match) => {
        console.log(`[Phase 4.1] Adding series: ${match[1]}`);
        return {
          properties: {
            _seriesOperation: {
              action: "add",
              seriesName: match[1],
            },
          },
        };
      }
    );

    // Remove series
    this.commandPatterns.set(
      /remove (?:the )?["']?([^"']+)["']? series/i,
      (match) => {
        console.log(`[Phase 4.1] Removing series: ${match[1]}`);
        return {
          properties: {
            _seriesOperation: {
              action: "remove",
              seriesName: match[1].trim(),
            },
          },
        };
      }
    );

    this.commandPatterns.set(
      /(?:delete |remove )series ["']([^"']+)["']/i,
      (match) => {
        console.log(`[Phase 4.1] Removing series: ${match[1]}`);
        return {
          properties: {
            _seriesOperation: {
              action: "remove",
              seriesName: match[1],
            },
          },
        };
      }
    );

    // Show/hide series
    this.commandPatterns.set(
      /hide (?:the )?["']?([^"']+)["']? series/i,
      (match) => {
        console.log(`[Phase 4.1] Hiding series: ${match[1]}`);
        return {
          properties: {
            _seriesOperation: {
              action: "hide",
              seriesName: match[1].trim(),
            },
          },
        };
      }
    );

    this.commandPatterns.set(
      /show (?:the )?["']?([^"']+)["']? series/i,
      (match) => {
        console.log(`[Phase 4.1] Showing series: ${match[1]}`);
        return {
          properties: {
            _seriesOperation: {
              action: "show",
              seriesName: match[1].trim(),
            },
          },
        };
      }
    );

    this.commandPatterns.set(
      /show only (?:the )?["']?([^"']+)["']?(?:\s+series)?/i,
      (match) => {
        console.log(`[Phase 4.1] Showing only series: ${match[1]}`);
        return {
          properties: {
            _seriesOperation: {
              action: "showOnly",
              seriesName: match[1].trim(),
            },
          },
        };
      }
    );

    // ========================================================================
    // Data Operations Patterns
    // ========================================================================

    this.commandPatterns.set(
      /refresh (?:the )?data/i,
      () => {
        console.log("[Phase 4.1] Refreshing data");
        return {
          properties: {
            _dataOperation: { type: "refresh" },
          },
        };
      }
    );

    this.commandPatterns.set(
      /generate (?:new )?sample data/i,
      () => {
        console.log("[Phase 4.1] Generating sample data");
        return {
          properties: {
            _dataOperation: { type: "generate" },
          },
        };
      }
    );

    this.commandPatterns.set(
      /clear (?:all )?data/i,
      () => {
        console.log("[Phase 4.1] Clearing data");
        return { data: [] };
      }
    );

    // ========================================================================
    // Raw Data Toggle Patterns
    // ========================================================================

    // Show raw/individual data points (no aggregation)
    this.commandPatterns.set(
      /(?:show|use|display|switch to)\s+(?:raw|individual|actual|real|unaggregated)\s+(?:data|values|readings|points)/i,
      () => {
        console.log("[Raw Data] Switching to raw data mode");
        return {
          properties: {
            _dataOperation: { type: "setAggregation", aggregation: "none", message: "Showing raw data points" }
          }
        };
      }
    );

    // Simple "raw data" command
    this.commandPatterns.set(
      /^raw\s+data$/i,
      () => {
        console.log("[Raw Data] Switching to raw data mode");
        return {
          properties: {
            _dataOperation: { type: "setAggregation", aggregation: "none", message: "Showing raw data" }
          }
        };
      }
    );

    // Switch to aggregated/averaged data
    this.commandPatterns.set(
      /(?:show|use|display|switch to)\s+(?:aggregated|averaged|summarized)\s+(?:data|values)/i,
      () => {
        console.log("[Raw Data] Switching to aggregated data mode");
        return {
          properties: {
            _dataOperation: { type: "setAggregation", aggregation: "avg", granularity: "hour", message: "Showing hourly average" }
          }
        };
      }
    );

    // Aggregate by specific granularity
    this.commandPatterns.set(
      /(?:aggregate|group|summarize)\s+(?:by|per)\s+(minute|hour|day|week|month)/i,
      (match) => {
        const granularity = match[1].toLowerCase();
        console.log(`[Raw Data] Aggregating by ${granularity}`);
        return {
          properties: {
            _dataOperation: { type: "setAggregation", aggregation: "avg", granularity, message: `Aggregating by ${granularity}` }
          }
        };
      }
    );

    // Show average/sum/min/max by granularity
    this.commandPatterns.set(
      /(?:show|use)\s+(?:the\s+)?(average|avg|sum|total|min|minimum|max|maximum)\s+(?:by|per)\s+(minute|hour|day|week|month)/i,
      (match) => {
        const aggMap: Record<string, string> = {
          'average': 'avg', 'avg': 'avg',
          'sum': 'sum', 'total': 'sum',
          'min': 'min', 'minimum': 'min',
          'max': 'max', 'maximum': 'max'
        };
        const aggregation = aggMap[match[1].toLowerCase()] || 'avg';
        const granularity = match[2].toLowerCase();
        console.log(`[Raw Data] Showing ${aggregation} by ${granularity}`);
        return {
          properties: {
            _dataOperation: { type: "setAggregation", aggregation, granularity, message: `Showing ${aggregation} by ${granularity}` }
          }
        };
      }
    );

    // ========================================================================
    // Phase 2.3: Font Size Patterns
    // ========================================================================

    // Axis font size - require "axis" keyword explicitly
    this.commandPatterns.set(
      /(?:set\s+)?axis\s+(?:font\s+)?size\s+(?:to\s+)?(\d+)(?:px)?$/i,
      (match) => {
        const size = parseInt(match[1], 10);
        console.log(`[Phase 2.3] Setting axis font size to ${size}`);
        return { properties: { axisFontSize: size } };
      }
    );

    // Legend font size
    this.commandPatterns.set(
      /(?:set\s+)?legend\s+(?:font\s+)?size\s+(?:to\s+)?(\d+)(?:px)?$/i,
      (match) => {
        const size = parseInt(match[1], 10);
        console.log(`[Phase 2.3] Setting legend font size to ${size}`);
        return { properties: { legendFontSize: size } };
      }
    );

    // Named font sizes
    this.commandPatterns.set(
      /(?:use\s+|make\s+)?(?:axis\s+|legend\s+)?(small|medium|large)\s+(?:font|text)/i,
      (match) => {
        const sizeMap: Record<string, number> = { small: 8, medium: 10, large: 12 };
        const size = sizeMap[match[1].toLowerCase()];
        console.log(`[Phase 2.3] Setting font size to ${size} (${match[1]})`);
        return { properties: { axisFontSize: size, legendFontSize: size } };
      }
    );

    // ========================================================================
    // Phase 5: Chart Navigation Patterns
    // ========================================================================

    // Pattern 1: Series Navigation - "When I click the Sales bar, go to Sales Details"
    this.commandPatterns.set(
      /when (?:I |you )?click (?:the |on )?["']?([^"']+?)["']? (?:bar|line|area|slice|point|series),?\s*(?:go|navigate|link) to (?:page )?["']?([^"']+)["']?/i,
      (match, component) => {
        console.log(`[Phase 5] Series navigation pattern matched: series="${match[1]}", page="${match[2]}"`);
        return this.handleNavigationCommand(match[1].trim(), match[2].trim(), 'series', component);
      }
    );

    // Pattern 2: Data Point Navigation - "Link pie slice 'Mobile' to Mobile Analytics"
    // Also matches: "link January slice to January Report"
    this.commandPatterns.set(
      /link (?:the )?(?:pie )?(?:slice |point |data(?:point)? )?["']?([^"']+?)["']? (?:slice |point )?to (?:page )?["']?([^"']+)["']?/i,
      (match, component) => {
        console.log(`[Phase 5] Data point navigation pattern matched: dataPoint="${match[1]}", page="${match[2]}"`);
        return this.handleNavigationCommand(match[1].trim(), match[2].trim(), 'dataPoint', component);
      }
    );

    // Pattern 3: Connect All Pattern - "Connect all bars to their detail pages"
    this.commandPatterns.set(
      /connect (?:all )?(?:the )?(?:bars?|lines?|series|slices?|points?) to (?:their )?(?:detail |respective )?pages?/i,
      (match, component) => {
        console.log(`[Phase 5] Auto-connect pattern matched`);
        return this.handleAutoNavigationCommand(component);
      }
    );

    // Pattern 4a: Enable Navigation - "Make chart clickable"
    this.commandPatterns.set(
      /make (?:the )?(?:chart )?(?:bars?|lines?|slices?|points?|elements?)? ?clickable/i,
      (match, component) => {
        console.log(`[Phase 5] Enable navigation pattern matched (clickable)`);
        return this.handleEnableNavigation(component);
      }
    );

    // Pattern 4b: Enable Navigation - "Enable chart navigation"
    this.commandPatterns.set(
      /enable (?:chart )?navigation/i,
      (match, component) => {
        console.log(`[Phase 5] Enable navigation pattern matched`);
        return this.handleEnableNavigation(component);
      }
    );

    // Pattern 5: Disable Navigation - "Remove chart navigation"
    this.commandPatterns.set(
      /(?:remove|disable|clear|turn off) (?:chart )?navigation/i,
      (match, component) => {
        console.log(`[Phase 5] Disable navigation pattern matched`);
        return this.handleRemoveNavigation();
      }
    );

    // Pattern 6: Set Default Navigation - "Set default navigation to Dashboard"
    this.commandPatterns.set(
      /(?:set )?default (?:chart )?navigation (?:to |page )?["']?([^"']+)["']?/i,
      (match, component) => {
        console.log(`[Phase 5] Set default navigation pattern matched: page="${match[1]}"`);
        return this.handleSetDefaultNavigation(match[1].trim(), component);
      }
    );

    // Pattern 7: Pass Data Parameters - "Pass clicked data to target page"
    this.commandPatterns.set(
      /pass (?:the )?(?:clicked )?data (?:as params|as parameters|to (?:the )?(?:target )?page)/i,
      (match, component) => {
        console.log(`[Phase 5] Pass data as params pattern matched`);
        return this.handlePassDataAsParams(component, true);
      }
    );

    // Pattern 7b: Stop passing data
    this.commandPatterns.set(
      /(?:stop|don't|do not) pass (?:the )?(?:clicked )?data/i,
      (match, component) => {
        console.log(`[Phase 5] Stop passing data pattern matched`);
        return this.handlePassDataAsParams(component, false);
      }
    );

    // Pattern 8: Remove Specific Mapping - "Remove navigation from Revenue series"
    this.commandPatterns.set(
      /remove (?:the )?navigation (?:from |for )(?:the )?["']?([^"']+?)["']?(?: series)?/i,
      (match, component) => {
        console.log(`[Phase 5] Remove mapping pattern matched: element="${match[1]}"`);
        return this.handleRemoveMapping(match[1].trim(), component);
      }
    );

    // Additional Pattern: Simple click navigation - "clicking Revenue goes to Revenue page"
    this.commandPatterns.set(
      /clicking (?:on )?(?:the )?["']?([^"']+?)["']? (?:goes|navigates|links) to (?:page )?["']?([^"']+)["']?/i,
      (match, component) => {
        console.log(`[Phase 5] Simple click navigation pattern: element="${match[1]}", page="${match[2]}"`);
        return this.handleNavigationCommand(match[1].trim(), match[2].trim(), 'series', component);
      }
    );

    // Additional Pattern: Drilldown - "add drilldown to Revenue Details"
    this.commandPatterns.set(
      /(?:add |enable )?drill[\s-]?down (?:to |for )(?:page )?["']?([^"']+)["']?/i,
      (match, component) => {
        console.log(`[Phase 5] Drilldown pattern matched: page="${match[1]}"`);
        return this.handleSetDefaultNavigation(match[1].trim(), component);
      }
    );

    logger.debug("ChartCommandHandler patterns initialized");
  }

  // ============================================================================
  // Phase 5: Chart Navigation Handler Methods
  // ============================================================================

  /**
   * Get series keys from chart component properties
   * Extracts barKeys, lineKeys, areaKeys, etc. based on chart type
   */
  private getSeriesKeysFromComponent(component: Component): string[] {
    const props = component.properties || {};
    const keys: string[] = [];

    // Check various series key properties based on chart type
    keys.push(...getStringArray(props.barKeys));
    keys.push(...getStringArray(props.lineKeys));
    keys.push(...getStringArray(props.areaKeys));
    keys.push(...getStringArray(props.dataKeys));

    // For pie charts, extract from data
    if (component.type === ComponentType.PIE_CHART) {
      const dataNames = getChartDataPoints(props.data)
        .map(d => d.name)
        .filter((name): name is string => typeof name === 'string');
      keys.push(...dataNames);
    }

    return [...new Set(keys)]; // Remove duplicates
  }

  /**
   * Handle series or data point navigation command
   * Creates/updates a ChartNavigationMapping
   */
  private handleNavigationCommand(
    elementName: string,
    pageName: string,
    matchType: 'series' | 'dataPoint',
    component: Component
  ): PropertyChange | null {
    // Resolve page target using inherited method
    const resolution = this.resolvePageTarget(pageName);

    if (resolution.error || !resolution.pageId) {
      console.log(`[Phase 5] Page resolution failed: ${resolution.error}`);
      // Return null so error can be handled in parseCommand
      return null;
    }

    // Get existing navigation config or create new one
    const existingNav = getChartNavigationConfig(component.properties?.navigation);
    const existingMappings = existingNav?.mappings || [];

    // Create the new mapping
    const newMapping: ChartNavigationMapping = {
      targetPageId: resolution.pageId,
      targetPageName: resolution.pageName || pageName,
      navigationLabel: resolution.pageName || pageName,
    };

    // Set the appropriate key based on match type
    if (matchType === 'series') {
      newMapping.seriesKey = elementName;
    } else {
      newMapping.dataPointValue = elementName;
    }

    // Check if mapping already exists and update it, or add new
    const existingIndex = existingMappings.findIndex(m =>
      (matchType === 'series' && m.seriesKey === elementName) ||
      (matchType === 'dataPoint' && m.dataPointValue === elementName)
    );

    let updatedMappings: ChartNavigationMapping[];
    if (existingIndex >= 0) {
      // Update existing mapping
      updatedMappings = [...existingMappings];
      updatedMappings[existingIndex] = { ...updatedMappings[existingIndex], ...newMapping };
    } else {
      // Add new mapping
      updatedMappings = [...existingMappings, newMapping];
    }

    const navigationConfig: ChartNavigationConfig = {
      enabled: true,
      mappings: updatedMappings,
      defaultAction: existingNav?.defaultAction || 'none',
      defaultTarget: existingNav?.defaultTarget,
      passDataAsParams: existingNav?.passDataAsParams,
    };

    console.log(`[Phase 5] Created navigation mapping: ${matchType}="${elementName}" -> page="${resolution.pageName}"`);

    return {
      properties: {
        navigation: navigationConfig,
      },
    };
  }

  /**
   * Handle auto-connect pattern - matches series keys to page names
   */
  private handleAutoNavigationCommand(component: Component): PropertyChange | null {
    const seriesKeys = this.getSeriesKeysFromComponent(component);

    if (seriesKeys.length === 0) {
      console.log(`[Phase 5] No series keys found for auto-connect`);
      return null;
    }

    const pages = useEditorStore.getState().pages || [];
    const currentPageId = useEditorStore.getState().currentPageId;
    const otherPages = pages.filter(p => p.id !== currentPageId);

    const mappings: ChartNavigationMapping[] = [];

    for (const seriesKey of seriesKeys) {
      // Try to find a matching page (fuzzy match)
      const matchedPage = otherPages.find(page => {
        const normalizedPageName = page.name.toLowerCase().replace(/[^a-z0-9]/g, '');
        const normalizedSeriesKey = seriesKey.toLowerCase().replace(/[^a-z0-9]/g, '');
        return normalizedPageName.includes(normalizedSeriesKey) ||
               normalizedSeriesKey.includes(normalizedPageName);
      });

      if (matchedPage) {
        mappings.push({
          seriesKey,
          targetPageId: matchedPage.id,
          targetPageName: matchedPage.name,
          navigationLabel: matchedPage.name,
        });
        console.log(`[Phase 5] Auto-matched series "${seriesKey}" to page "${matchedPage.name}"`);
      }
    }

    if (mappings.length === 0) {
      console.log(`[Phase 5] No pages matched series keys`);
      return null;
    }

    const existingNav = getChartNavigationConfig(component.properties?.navigation);

    const navigationConfig: ChartNavigationConfig = {
      enabled: true,
      mappings,
      defaultAction: existingNav?.defaultAction || 'none',
      defaultTarget: existingNav?.defaultTarget,
      passDataAsParams: existingNav?.passDataAsParams,
    };

    return {
      properties: {
        navigation: navigationConfig,
      },
    };
  }

  /**
   * Handle enable navigation command
   */
  private handleEnableNavigation(component: Component): PropertyChange {
    const existingNav = getChartNavigationConfig(component.properties?.navigation);

    const navigationConfig: ChartNavigationConfig = {
      enabled: true,
      mappings: existingNav?.mappings || [],
      defaultAction: existingNav?.defaultAction || 'none',
      defaultTarget: existingNav?.defaultTarget,
      passDataAsParams: existingNav?.passDataAsParams,
    };

    return {
      properties: {
        navigation: navigationConfig,
      },
    };
  }

  /**
   * Handle remove/disable navigation command
   */
  private handleRemoveNavigation(): PropertyChange {
    const navigationConfig: ChartNavigationConfig = {
      enabled: false,
      mappings: [],
    };

    return {
      properties: {
        navigation: navigationConfig,
      },
    };
  }

  /**
   * Handle set default navigation target
   */
  private handleSetDefaultNavigation(pageName: string, component: Component): PropertyChange | null {
    const resolution = this.resolvePageTarget(pageName);

    if (resolution.error || !resolution.pageId) {
      console.log(`[Phase 5] Default page resolution failed: ${resolution.error}`);
      return null;
    }

    const existingNav = getChartNavigationConfig(component.properties?.navigation);

    const navigationConfig: ChartNavigationConfig = {
      enabled: true,
      mappings: existingNav?.mappings || [],
      defaultAction: 'navigate',
      defaultTarget: resolution.pageId,
      passDataAsParams: existingNav?.passDataAsParams,
    };

    console.log(`[Phase 5] Set default navigation to: ${resolution.pageName}`);

    return {
      properties: {
        navigation: navigationConfig,
      },
    };
  }

  /**
   * Handle pass data as params toggle
   */
  private handlePassDataAsParams(component: Component, passData: boolean): PropertyChange {
    const existingNav = getChartNavigationConfig(component.properties?.navigation);

    const navigationConfig: ChartNavigationConfig = {
      enabled: existingNav?.enabled ?? true,
      mappings: existingNav?.mappings || [],
      defaultAction: existingNav?.defaultAction || 'none',
      defaultTarget: existingNav?.defaultTarget,
      passDataAsParams: passData,
    };

    return {
      properties: {
        navigation: navigationConfig,
      },
    };
  }

  /**
   * Handle remove specific mapping command
   */
  private handleRemoveMapping(elementName: string, component: Component): PropertyChange | null {
    const existingNav = getChartNavigationConfig(component.properties?.navigation);

    if (!existingNav?.mappings || existingNav.mappings.length === 0) {
      console.log(`[Phase 5] No mappings to remove`);
      return null;
    }

    // Filter out the mapping that matches the element name
    const updatedMappings = existingNav.mappings.filter(m =>
      m.seriesKey !== elementName && m.dataPointValue !== elementName
    );

    if (updatedMappings.length === existingNav.mappings.length) {
      console.log(`[Phase 5] No mapping found for element "${elementName}"`);
      return null;
    }

    // Auto-disable if no mappings remain and no default target
    const shouldDisable = updatedMappings.length === 0 && !existingNav.defaultTarget;

    const navigationConfig: ChartNavigationConfig = {
      enabled: shouldDisable ? false : existingNav.enabled,
      mappings: updatedMappings,
      defaultAction: existingNav.defaultAction,
      defaultTarget: existingNav.defaultTarget,
      passDataAsParams: existingNav.passDataAsParams,
    };

    console.log(`[Phase 5] Removed mapping for "${elementName}", ${updatedMappings.length} mappings remaining`);

    return {
      properties: {
        navigation: navigationConfig,
      },
    };
  }

  // ============================================================================
  // Phase 2.1: Responsive Breakpoint Handlers
  // ============================================================================

  /**
   * Phase 2.1: Handle breakpoint-prefixed chart commands
   * Parses the sub-command using existing patterns and wraps result in responsive config
   *
   * @param breakpointStr - 'mobile', 'tablet', or 'desktop'
   * @param subCommand - The chart command to apply at that breakpoint
   * @param component - The chart component
   * @returns PropertyChange with responsiveChartConfig or null if sub-command not recognized
   */
  private handleChartBreakpointCommand(
    breakpointStr: string,
    subCommand: string,
    component: Component
  ): PropertyChange | null {
    const breakpoint = breakpointStr.toLowerCase() as 'mobile' | 'tablet' | 'desktop';
    const trimmedSubCommand = subCommand.trim();

    console.log(`[Phase 2.1] handleChartBreakpointCommand called`);
    console.log(`[Phase 2.1] Breakpoint: ${breakpoint}`);
    console.log(`[Phase 2.1] Sub-command: "${trimmedSubCommand}"`);

    // Check for preset patterns first
    const presetMatch = trimmedSubCommand.match(/^(compact|minimal|comfortable|detailed|full)(?:\s+(?:chart|mode|layout))?$/i);
    if (presetMatch) {
      const preset = presetMatch[1].toLowerCase();
      const presetConfig = RESPONSIVE_CHART_PRESETS[preset];
      if (presetConfig) {
        console.log(`[Phase 2.1] Applied preset "${preset}" for ${breakpoint}:`, presetConfig);
        return {
          responsiveChartConfig: { [breakpoint]: { ...presetConfig } }
        };
      }
    }

    // Parse sub-command using existing patterns (excluding breakpoint patterns)
    const subResult = this.parseChartSubCommand(trimmedSubCommand, component);

    if (!subResult) {
      console.log(`[Phase 2.1] No changes parsed for subCommand: "${trimmedSubCommand}"`);
      return null;
    }

    console.log(`[Phase 2.1] Sub-command parsed successfully:`, JSON.stringify(subResult));

    // Convert PropertyChange to ChartResponsiveConfig
    const responsiveConfig: Record<string, unknown> = {};

    // Map chart properties to responsive config
    if (subResult.properties) {
      // Visibility toggles
      if (subResult.properties.showLegend !== undefined) {
        responsiveConfig.showLegend = subResult.properties.showLegend;
      }
      if (subResult.properties.showGrid !== undefined) {
        responsiveConfig.showGrid = subResult.properties.showGrid;
      }
      if (subResult.properties.showTooltip !== undefined) {
        responsiveConfig.showTooltip = subResult.properties.showTooltip;
      }
      if (subResult.properties.showXAxis !== undefined) {
        responsiveConfig.showXAxis = subResult.properties.showXAxis;
      }
      if (subResult.properties.showYAxis !== undefined) {
        responsiveConfig.showYAxis = subResult.properties.showYAxis;
      }
      if (subResult.properties.showDots !== undefined) {
        responsiveConfig.showDots = subResult.properties.showDots;
      }

      // Size-related properties
      if (subResult.properties.strokeWidth !== undefined) {
        responsiveConfig.strokeWidth = subResult.properties.strokeWidth;
      }
      if (subResult.properties.dotSize !== undefined) {
        responsiveConfig.dotSize = subResult.properties.dotSize;
      }
      if (subResult.properties.axisFontSize !== undefined) {
        responsiveConfig.axisFontSize = subResult.properties.axisFontSize;
      }
      if (subResult.properties.legendFontSize !== undefined) {
        responsiveConfig.legendFontSize = subResult.properties.legendFontSize;
      }

      // Margin
      if (subResult.properties.margin !== undefined) {
        responsiveConfig.margin = subResult.properties.margin;
      }

      // Title
      if (subResult.properties.title !== undefined) {
        responsiveConfig.title = subResult.properties.title;
      }

      // Line style properties (Phase 1)
      if (subResult.properties.strokeDasharray !== undefined) {
        responsiveConfig.strokeDasharray = subResult.properties.strokeDasharray;
      }
      if (subResult.properties.strokeOpacity !== undefined) {
        responsiveConfig.strokeOpacity = subResult.properties.strokeOpacity;
      }
      if (subResult.properties.lineType !== undefined) {
        responsiveConfig.lineType = subResult.properties.lineType;
      }
    }

    // Map style changes
    if (subResult.style) {
      responsiveConfig.style = subResult.style;
    }

    console.log(`[Phase 2.1] Responsive config for ${breakpoint}:`, JSON.stringify(responsiveConfig));

    return {
      responsiveChartConfig: { [breakpoint]: responsiveConfig }
    };
  }

  /**
   * Phase 2.1: Parse chart sub-command without triggering breakpoint patterns
   * This prevents infinite recursion when parsing breakpoint-prefixed commands
   *
   * @param command - The command to parse (without breakpoint prefix)
   * @param component - The chart component
   * @returns PropertyChange or null if no pattern matched
   */
  private parseChartSubCommand(command: string, component: Component): PropertyChange | null {
    const normalizedCommand = command.trim().toLowerCase();

    console.log(`[Phase 2.1] parseChartSubCommand: "${normalizedCommand}"`);

    // Check if this looks like a nested breakpoint command (prevent recursion)
    const breakpointPrefixPatterns = [
      /^on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop):\s*/i,
      /^only\s+on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop)\s+(hide|show|legend|grid|title|axis|dots|markers|thick|thin|small|large|compact|minimal|detailed|full)\b/i
    ];

    for (const pattern of breakpointPrefixPatterns) {
      if (pattern.test(normalizedCommand)) {
        console.log(`[Phase 2.1] Nested breakpoint pattern detected, skipping to prevent recursion`);
        return null;
      }
    }

    // Match against existing patterns, skipping breakpoint patterns
    for (const [pattern, handler] of this.commandPatterns) {
      // Skip patterns that contain breakpoint references (our Phase 2.1 patterns)
      const patternSource = pattern.source;
      if (
        patternSource.includes('mobile|tablet|desktop') ||
        (patternSource.includes('on\\s+') && patternSource.includes('mobile'))
      ) {
        continue;
      }

      const match = normalizedCommand.match(pattern);
      if (match) {
        console.log(`[Phase 2.1] Sub-command matched pattern: ${pattern.source.substring(0, 50)}...`);
        const result = handler(match, component);
        if (result) {
          return result;
        }
      }
    }

    console.log(`[Phase 2.1] No pattern matched sub-command: "${normalizedCommand}"`);
    return null;
  }

  canHandle(command: string, component: Component): boolean {
    const isChart = chartTypeRegistry.isChartType(component.type);
    const hasChartKeywords = this.hasChartKeywords(command);

    if (isChart && hasChartKeywords) {
      console.log(`[Phase 4.1] ChartCommandHandler can handle: "${command}" for ${component.type}`);
    }

    return isChart && hasChartKeywords;
  }

  private hasChartKeywords(command: string): boolean {
    const chartKeywords = [
      // Data operations
      "data", "point", "value", "add", "remove", "update", "clear", "generate", "refresh",
      // Visual elements
      "title", "legend", "grid", "axis", "label", "tooltip",
      // Styling
      "color", "theme", "style", "width", "thickness", "palette",
      // Chart types
      "line", "bar", "pie", "area", "scatter", "chart", "gauge", "donut",
      // Line specific
      "dot", "marker", "smooth", "curve", "dashed", "solid", "dotted",
      // Conversion
      "convert", "transform", "change to", "show as", "switch",
      // Bar specific
      "stack", "horizontal", "vertical", "grouped",
      // Pie specific
      "percentage", "slice", "inner", "radius",
      // Series
      "series", "hide series", "show series",
      // Animation
      "animation", "animate",
      // Axes and gauge properties
      "minimum", "maximum", "min", "max", "range", "start", "end", "unit",
      // Phase 2: Responsive breakpoints
      "mobile", "tablet", "desktop", "compact", "minimal", "detailed", "comfortable",
      // Phase 2.3: Font size
      "font", "text", "small font", "large font",
      // Phase 5: Navigation keywords
      "navigate", "navigation", "click", "clickable", "clicking", "link", "connect",
      "drill", "drilldown", "drill-down", "detail", "go to", "goes to",
      // Gauge threshold keywords
      "threshold", "thresholds", "zones", "color zone",
    ];

    const lowerCommand = command.toLowerCase();
    return chartKeywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const lowerCommand = command.toLowerCase();
    console.log(`[Phase 4.1] Parsing chart command: "${command}" for ${component.type}`);

    // Check for templates first
    const template = chartTemplateManager.findTemplateByCommand(command);
    if (template) {
      const templateConfig = chartTemplateManager.applyTemplate(template);
      console.log(`[Phase 4.1] Applied template: ${template.name}`);
      return {
        success: true,
        command: {
          type: "template",
          changes: {
            type: templateConfig.type,
            properties: templateConfig.properties,
            data: templateConfig.data,
          },
          confidence: 0.9,
          description: `Applied template: ${template.name}`,
        },
        suggestions: await this.getSuggestions(component),
      };
    }

    // Check for chart type conversion
    if (this.isConversionCommand(lowerCommand)) {
      const conversionResult = await this.handleChartConversion(command, component);
      if (conversionResult.success) {
        return conversionResult;
      }
    }

    // Parse data operations
    const currentData = getChartDataPoints(component.properties?.data);
    const dataOperation = chartDataManager.parseDataCommand(command, currentData);
    if (dataOperation) {
      // Determine chart type for title generation
      const chartTypeMap: Record<string, 'line' | 'bar' | 'area' | undefined> = {
        [ComponentType.LINE_CHART]: 'line',
        [ComponentType.BAR_CHART]: 'bar',
        [ComponentType.AREA_CHART]: 'area',
      };
      const chartType = chartTypeMap[component.type];

      // Apply data operation with automatic title generation trigger
      const newData = chartDataManager.applyDataOperationWithTitleUpdate(
        currentData,
        dataOperation,
        component.id,
        chartType
      );
      console.log(`[Phase 4.1] Data operation: ${dataOperation.type}, new data length: ${newData.length}`);
      return {
        success: true,
        command: {
          type: "data",
          changes: { data: newData },
          confidence: 0.85,
          description: this.generateDataDescription(dataOperation, newData),
        },
        suggestions: await this.getSuggestions(component),
      };
    }

    // Split complex commands
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      // Try pattern matching
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
        if (patternMatch.data !== undefined) {
          changes.data = patternMatch.data;
        }
        // Phase 2: Handle responsiveChartConfig from breakpoint patterns
        if (patternMatch.responsiveChartConfig) {
          changes.responsiveChartConfig = {
            ...changes.responsiveChartConfig,
            ...patternMatch.responsiveChartConfig,
          };
          console.log('[Phase 2.1] Merged responsiveChartConfig into changes:', changes.responsiveChartConfig);
        }
      }

      // Parse hex colors not caught by patterns
      const hexMatch = part.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/g);
      if (hexMatch && (!changes.properties || !changes.properties.colors)) {
        changes.properties = { ...changes.properties, colors: hexMatch };
        console.log(`[Phase 4.1] Extracted hex colors: ${hexMatch.join(", ")}`);
      }
    }

    // Check if we found any changes
    if (
      Object.keys(changes.properties || {}).length > 0 ||
      Object.keys(changes.style || {}).length > 0 ||
      changes.data !== undefined ||
      changes.responsiveChartConfig !== undefined
    ) {
      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component),
        };
      }

      console.log(`[Phase 4.1] Successfully parsed chart command with changes:`, changes);
      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component),
        },
        suggestions: await this.getSuggestions(component),
      };
    }

    // Phase 5: Check if this looks like a navigation command that failed page resolution
    const navigationPatterns = [
      /when (?:I |you )?click.*(?:go|navigate|link) to/i,
      /link (?:the )?.*to (?:page )?/i,
      /clicking .*(?:goes|navigates|links) to/i,
      /(?:set )?default (?:chart )?navigation/i,
      /(?:add |enable )?drill[\s-]?down/i,
    ];

    const isFailedNavigation = navigationPatterns.some(p => p.test(command));
    if (isFailedNavigation) {
      // Try to extract the page name to give a better error
      const pageMatch = command.match(/(?:to |page )["']?([^"']+)["']?\s*$/i);
      const pageName = pageMatch?.[1]?.trim();

      if (pageName) {
        const resolution = this.resolvePageTarget(pageName);
        return {
          success: false,
          error: resolution.error || `Could not find page "${pageName}" for navigation`,
          suggestions: await this.getSuggestions(component),
        };
      }
    }

    console.log(`[Phase 4.1] Could not parse chart command: "${command}"`);
    return {
      success: false,
      error: "Could not understand the chart command",
      suggestions: await this.getSuggestions(component),
    };
  }

  // ============================================================================
  // Phase 4.1.3: Chart Type Conversion
  // ============================================================================

  private isConversionCommand(command: string): boolean {
    const conversionPatterns = [
      /convert\s+(?:to|into)/i,
      /change\s+(?:to|into)\s+(?:a\s+)?(?:line|bar|pie|area|scatter)/i,
      /switch\s+to/i,
      /make\s+(?:it\s+)?(?:a\s+)?(?:line|bar|pie|area|scatter)/i,
      /show\s+(?:as|like)\s+(?:a\s+)?(?:line|bar|pie|area|scatter)/i,
      /transform\s+(?:to|into)/i,
    ];
    return conversionPatterns.some(pattern => pattern.test(command));
  }

  private async handleChartConversion(command: string, component: Component): Promise<CommandResult> {
    const targetType = this.parseConversionTarget(command);

    if (!targetType) {
      return {
        success: false,
        error: "Could not determine target chart type",
        suggestions: await this.getSuggestions(component),
      };
    }

    if (targetType === component.type) {
      return {
        success: false,
        error: `Chart is already a ${chartTypeRegistry.getConfig(targetType)?.name || targetType}`,
        suggestions: await this.getSuggestions(component),
      };
    }

    // Check if conversion is supported
    if (!chartTypeRegistry.canConvertTo(component.type, targetType)) {
      return {
        success: false,
        error: `Cannot convert from ${component.type} to ${targetType}`,
        suggestions: await this.getSuggestions(component),
      };
    }

    // Get conversion configuration
    const conversionKey = `${this.getShortType(component.type)}-${this.getShortType(targetType)}`;
    const conversionConfig = CHART_CONVERSIONS[conversionKey];

    const changes: PropertyChange = { type: targetType };
    const warnings: string[] = [];

    // Transform data if required (skip empty arrays to avoid validation rejection)
    const existingData = getChartDataPoints(component.properties?.data);
    if (existingData.length > 0) {
      changes.data = chartDataManager.transformDataForChartType(
        existingData,
        component.type,
        targetType
      );
    }

    // Apply property mappings
    if (conversionConfig) {
      changes.properties = {};

      // Preserve compatible properties
      for (const prop of conversionConfig.preservedProperties) {
        if (component.properties?.[prop] !== undefined) {
          changes.properties[prop] = component.properties[prop];
        }
      }

      // Apply property mappings
      for (const [from, to] of Object.entries(conversionConfig.propertyMappings)) {
        if (component.properties?.[from] !== undefined) {
          changes.properties[to] = component.properties[from];
        }
      }

      warnings.push(...conversionConfig.warnings);
    }

    // Remap value key properties (lineKeys ↔ barKeys ↔ areaKeys)
    const VALUE_KEY_MAP: Record<string, string> = {
      'line-chart': 'lineKeys',
      'bar-chart': 'barKeys',
      'area-chart': 'areaKeys',
    };

    const sourceKeyProp = VALUE_KEY_MAP[component.type];
    const targetKeyProp = VALUE_KEY_MAP[targetType];

    if (sourceKeyProp && targetKeyProp && sourceKeyProp !== targetKeyProp) {
      const sourceKeys = component.properties?.[sourceKeyProp];
      if (sourceKeys) {
        if (!changes.properties) changes.properties = {};
        changes.properties[targetKeyProp] = sourceKeys;
        changes.properties[sourceKeyProp] = undefined;
      }
    }

    // Clean up chart-type-specific properties from the old type
    const CHART_TYPE_SPECIFIC_PROPS: Record<string, string[]> = {
      'line-chart': ['showDots', 'dotSize', 'lineType', 'strokeDasharray', 'connectNulls', 'area', 'areaOpacity', 'tension'],
      'bar-chart': ['cornerRadius', 'barGap', 'barCategoryGap', 'orientation'],
      'area-chart': ['fillOpacity'],
    };

    const oldSpecificProps = CHART_TYPE_SPECIFIC_PROPS[component.type] || [];
    if (oldSpecificProps.length > 0) {
      if (!changes.properties) changes.properties = {};
      for (const prop of oldSpecificProps) {
        changes.properties[prop] = undefined;
      }
    }

    const targetName = chartTypeRegistry.getConfig(targetType)?.name || targetType;
    let description = `Converted to ${targetName}`;
    if (warnings.length > 0) {
      description += `. Note: ${warnings.join("; ")}`;
    }

    console.log(`[Phase 4.1] Chart conversion: ${component.type} -> ${targetType}`);

    return {
      success: true,
      command: {
        type: "conversion",
        changes,
        confidence: 0.85,
        description,
      },
      suggestions: await this.getSuggestions(component),
    };
  }

  private parseConversionTarget(command: string): ComponentType | null {
    const chartTypes: Record<string, ComponentType> = {
      line: ComponentType.LINE_CHART,
      bar: ComponentType.BAR_CHART,
      pie: ComponentType.PIE_CHART,
      area: ComponentType.AREA_CHART,
      scatter: ComponentType.SCATTER_CHART,
      column: ComponentType.BAR_CHART,
      donut: ComponentType.PIE_CHART,
      gauge: ComponentType.GAUGE_CHART,
      'vertical-gauge': ComponentType.VERTICAL_GAUGE_CHART,
      heatmap: ComponentType.HEATMAP_CHART,
      treemap: ComponentType.TREEMAP_CHART,
    };

    // Match "to X chart" or "into X chart" or "as X"
    const patterns = [
      /(?:to|into)\s+(?:a\s+)?(\w+)\s*(?:chart)?/i,
      /show\s+(?:as|like)\s+(?:a\s+)?(\w+)/i,
      /make\s+(?:it\s+)?(?:a\s+)?(\w+)\s*(?:chart)?/i,
    ];

    for (const pattern of patterns) {
      const match = command.match(pattern);
      if (match) {
        const targetType = match[1].toLowerCase();
        for (const [keyword, type] of Object.entries(chartTypes)) {
          if (targetType.includes(keyword) || keyword.includes(targetType)) {
            return type;
          }
        }
      }
    }

    return null;
  }

  private getShortType(type: ComponentType): string {
    const mapping: Record<string, string> = {
      [ComponentType.LINE_CHART]: "line",
      [ComponentType.BAR_CHART]: "bar",
      [ComponentType.PIE_CHART]: "pie",
      [ComponentType.AREA_CHART]: "area",
      [ComponentType.SCATTER_CHART]: "scatter",
      [ComponentType.GAUGE_CHART]: "gauge",
      [ComponentType.VERTICAL_GAUGE_CHART]: "vertical-gauge",
      [ComponentType.HEATMAP_CHART]: "heatmap",
      [ComponentType.TREEMAP_CHART]: "treemap",
    };
    return mapping[type] || type;
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private parseChartColorList(value: string): string[] {
    const cleanedValue = value.trim().replace(/^\[|\]$/g, "");
    const themeName = cleanedValue.toLowerCase().replace(/^["']|["']$/g, "");
    const resolvedTheme = THEME_ALIASES[themeName] || themeName;

    if (CHART_THEMES[resolvedTheme]) {
      return CHART_THEMES[resolvedTheme];
    }

    const colors = cleanedValue
      .match(/#[0-9A-Fa-f]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)|[a-zA-Z]+(?:-[a-zA-Z]+)?/g)
      ?.map(color => color.trim().replace(/^["']|["']$/g, ""))
      .map(color => this.parseColorValue(color) || (/^hsla?\(/i.test(color) ? color : null))
      .filter((color): color is string => Boolean(color)) || [];

    return [...new Set(colors)];
  }

  async getExampleCommands(componentType: ComponentType): Promise<string[]> {
    if (!componentType || !chartTypeRegistry.isChartType(componentType)) return [];

    const config = chartTypeRegistry.getConfig(componentType);
    const examples = config?.aiCommands || [];

    // Common examples for all charts
    const commonExamples = [
      'Set title to "Monthly Sales"',
      'Change x-axis label to "Month"',
      'Change y-axis label to "Revenue"',
      "Use blue theme",
      "Apply agricultural colors",
      "Show legend",
      "Hide grid lines",
      "Enable animation",
      "Generate sample data",
      'Add series "Revenue"',
      "Convert to bar chart",
      // Phase 5: Navigation examples
      "When I click the Revenue bar, go to Revenue Details page",
      "Link pie slice to detail page",
      "Make chart bars clickable",
      "Set default navigation to Dashboard",
      "Connect all bars to their detail pages",
      "Remove chart navigation",
    ];

    // Type-specific examples
    const typeSpecific: Partial<Record<ComponentType, string[]>> = {
      [ComponentType.LINE_CHART]: [
        "Make the line thicker",
        "Use dashed line",
        "Show data points",
        "Smooth the curve",
        "Start y-axis at 0",
      ],
      [ComponentType.BAR_CHART]: [
        "Stack the bars",
        "Make horizontal bars",
        "Group the bars",
        "Set bar gap to 10",
      ],
      [ComponentType.PIE_CHART]: [
        "Make it a donut chart",
        "Show percentage labels",
        "Set inner radius to 50",
        "Show value labels",
      ],
      [ComponentType.AREA_CHART]: [
        "Fill area with gradient",
        "Stack areas",
        "Show data points",
      ],
      [ComponentType.SCATTER_CHART]: [
        "Increase point size",
        "Add trend line",
        "Show data labels",
      ],
      [ComponentType.GAUGE_CHART]: [
        "Set value to 75",
        "Set maximum to 100",
        "Set minimum to 0",
        "Add thresholds red 0-33 yellow 33-66 green 66-100",
        "Switch to progress ring",
        "Switch to progress bar",
        "Change to milestone mode",
        "Show as fraction {value}/{max}",
      ],
      [ComponentType.HEATMAP_CHART]: [
        "Use warm colors",
        "Apply cool theme",
      ],
      [ComponentType.TREEMAP_CHART]: [
        "Show value labels",
        "Use forest theme",
      ],
    };

    return [...new Set([...commonExamples, ...examples, ...(typeSpecific[componentType] || [])])];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const hasData =
      component.properties?.data &&
      Array.isArray(component.properties.data) &&
      component.properties.data.length > 0;

    // Data suggestions
    if (!hasData) {
      suggestions.push("Generate sample data", "Add monthly data", "Import data");
    } else {
      suggestions.push("Add more data points", "Clear all data", "Update last value");
    }

    // Title suggestions
    if (!component.properties?.title) {
      suggestions.push('Set title to "Chart Title"');
    }

    // Visual suggestions
    if (component.properties?.showLegend !== false) {
      suggestions.push("Hide legend");
    } else {
      suggestions.push("Show legend");
    }

    if (component.properties?.showGrid !== false) {
      suggestions.push("Hide grid lines");
    } else {
      suggestions.push("Show grid lines");
    }

    // Color suggestions
    suggestions.push("Use blue theme", "Apply agricultural colors", "Use rainbow colors");

    // Chart type conversion
    const relatedTypes = chartTypeRegistry.getRelatedTypes(component.type);
    relatedTypes.forEach(type => {
      const config = chartTypeRegistry.getConfig(type);
      if (config) {
        suggestions.push(`Convert to ${config.name.toLowerCase()}`);
      }
    });

    // Type-specific suggestions
    if (component.type === ComponentType.LINE_CHART) {
      suggestions.push("Make line dashed", "Show data points", "Make line thicker");
    } else if (component.type === ComponentType.BAR_CHART) {
      suggestions.push("Stack the bars", "Make horizontal", "Group the bars");
    } else if (component.type === ComponentType.PIE_CHART) {
      suggestions.push("Convert to donut chart", "Show percentages");
    } else if (component.type === ComponentType.GAUGE_CHART) {
      const mode = component.properties?.displayMode || 'dial';
      suggestions.push("Set value to 50", "Set maximum to 100", "Add color thresholds");
      if (mode === 'dial') suggestions.push("Switch to progress ring", "Switch to progress bar");
      if (mode === 'ring') suggestions.push("Add concentric rings", "Switch to bar");
      if (mode === 'bar') suggestions.push("Add stacked segments", "Switch to ring");
      if (mode === 'milestone') suggestions.push("Switch to dial", "Switch to ring");
    }

    // Animation suggestion
    if (component.properties?.animation !== false) {
      suggestions.push("Disable animation");
    } else {
      suggestions.push("Enable animation");
    }

    // Phase 5: Navigation suggestions
    const nav = getChartNavigationConfig(component.properties?.navigation);
    const pages = useEditorStore.getState().pages || [];
    const currentPageId = useEditorStore.getState().currentPageId;
    const otherPages = pages.filter(p => p.id !== currentPageId);

    if (!nav?.enabled) {
      suggestions.push("Make chart clickable");
      if (otherPages.length > 0) {
        suggestions.push("Enable chart navigation");
      }
    } else {
      // Navigation is enabled
      if (otherPages.length > 0) {
        suggestions.push("Add navigation mapping");
      }
      suggestions.push("Remove chart navigation");

      if (!nav.passDataAsParams) {
        suggestions.push("Pass clicked data to target page");
      }

      if (!nav.defaultTarget && otherPages.length > 0) {
        suggestions.push(`Set default navigation to ${otherPages[0].name}`);
      }
    }

    return suggestions.slice(0, 10);
  }

  protected override validateChanges(
    changes: PropertyChange,
    component: Component
  ): { valid: boolean; error?: string } {
    // Validate data if present
    if (changes.data) {
      const data = getChartDataPoints(changes.data);
      if (data.length !== changes.data.length) {
        return {
          valid: false,
          error: "Data validation failed: chart data must be an array of objects",
        };
      }

      const validation = chartDataManager.validateDataForChartType(
        data,
        component.type
      );
      if (!validation.valid) {
        return {
          valid: false,
          error: `Data validation failed: ${validation.errors.join(", ")}`,
        };
      }
    }

    // Validate bar-chart-specific properties
    if (changes.properties) {
      // Stacking is only available for bar charts
      if (changes.properties.stacked !== undefined) {
        const isBarChart = component.type === 'bar-chart';
        if (!isBarChart) {
          console.log(`[Phase 4.1] Stacking validation failed: ${component.type} is not a bar chart`);
          return {
            valid: false,
            error: `Stacking is only available for bar charts. This component is a ${component.type}.`,
          };
        }
      }

      // Validate properties against schema
      for (const [key, value] of Object.entries(changes.properties)) {
        // Skip internal operation markers
        if (key.startsWith("_")) continue;

        if (!chartTypeRegistry.validateProperty(component.type, key, value)) {
          // Don't fail, just log - some properties may be custom
          console.log(`[Phase 4.1] Property "${key}" validation: not in schema, allowing anyway`);
        }
      }
    }

    // Validate chart type conversion
    if (changes.type) {
      if (!chartTypeRegistry.canConvertTo(component.type, changes.type)) {
        return {
          valid: false,
          error: `Cannot convert from ${component.type} to ${changes.type}`,
        };
      }
    }

    return super.validateChanges(changes, component);
  }

  private generateDataDescription(
    operation: { type: string; point?: ChartDataPoint; count?: number },
    data: ChartDataPoint[]
  ): string {
    if (operation.type === "add" && operation.point) {
      return `Added data point: ${operation.point.name} = ${operation.point.value}`;
    } else if (operation.type === "remove") {
      return `Removed ${operation.count || 1} data point(s)`;
    } else if (operation.type === "clear") {
      return "Cleared all data";
    } else if (operation.type === "generate") {
      return `Generated ${data.length} sample data points`;
    }
    return "Updated chart data";
  }
}

export const chartCommandHandler = new ChartCommandHandler();
